// 2026-06-26: Phase 14 — NADCAP Compliance Dashboard
// One-page roll-up of compliance state across all NADCAP Core capabilities.
// Pulls from: calibration_register, operator_qualifications, heat_treat_cycles,
// material_certs (customer attrs), and the mes_audit_log table.
import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { NavLink } from 'react-router-dom';
import axios from 'axios';
import { customerbasedshift, cleanCustomerId } from '../../Services/app/operatorservice';

const BR = {
  orange: '#f97316', blue: '#2563eb', green: '#16a34a', amber: '#f59e0b', red: '#dc2626',
  purple: '#7c3aed', dark: '#111827', muted: '#6b7280', border: '#e5e7eb', bg: '#f9fafb',
};

function StatusPill({ status, value }) {
  const map = {
    OK:       { bg: '#dcfce7', color: BR.green, label: 'OK' },
    READY:    { bg: '#dbeafe', color: BR.blue,  label: 'READY' },
    REVIEW:   { bg: '#fef3c7', color: BR.amber, label: 'REVIEW' },
    ACTION:   { bg: '#fee2e2', color: BR.red,   label: 'ACTION' },
  };
  const c = map[status] || map.READY;
  return (
    <span style={{
      padding: '2px 8px', borderRadius: 12, fontSize: 10, fontWeight: 800, letterSpacing: 0.4,
      background: c.bg, color: c.color, border: `1px solid ${c.color}`,
    }}>{c.label}{value != null ? ` · ${value}` : ''}</span>
  );
}

function fmtDate(d) {
  if (!d) return '—';
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return '—';
  return dt.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }) + ' ' +
         dt.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: false });
}

function daysFromNow(d) {
  if (!d) return null;
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return null;
  return Math.round((dt - new Date()) / 86400000);
}

const ACTION_LABEL = {
  OP_START: 'Operation started',
  OP_CLAIM_PARTIAL: 'Partial claim',
  OP_CLAIM_COMPLETE: 'Claim complete',
  OP_PAUSE: 'Paused',
  OP_RESUME: 'Resumed',
  QA_SIGNOFF: 'QA sign-off',
  QUAL_CHECK_WARN: '⚠ Cert warning',
  NC_CREATED: 'NC raised',
  RCCA_SIGNOFF: 'RCCA sign-off',
  CAL_OVERDUE_FLAG: '⚠ Calibration flag',
  MAT_CERT_LINKED: 'Material cert linked',
  SYSTEM_BOOT: 'System boot',
};

const ACTION_COLOR = {
  QA_SIGNOFF: BR.green, QUAL_CHECK_WARN: BR.red, CAL_OVERDUE_FLAG: BR.red,
  NC_CREATED: BR.red, RCCA_SIGNOFF: BR.green,
  OP_PAUSE: BR.orange, OP_RESUME: BR.blue,
  OP_CLAIM_COMPLETE: BR.green, OP_CLAIM_PARTIAL: BR.amber, OP_START: BR.blue,
  MAT_CERT_LINKED: BR.blue, SYSTEM_BOOT: BR.muted,
};

export default function NadcapDashboard() {
  const customerId = cleanCustomerId(localStorage.getItem('CustomerID') || window._env_.CUSTOMER_ID);
  const BASE = (window._env_?.SERVER_URL2 || '').replace(/\/$/, '');

  const [cal, setCal] = useState([]);
  const [quals, setQuals] = useState([]);
  const [cycles, setCycles] = useState([]);
  const [certs, setCerts] = useState([]);
  const [ndt, setNdt] = useState([]);
  const [anodize, setAnodize] = useState([]);
  const [followers, setFollowers] = useState([]);
  const [routeCards, setRouteCards] = useState([]);
  const [jigs, setJigs] = useState([]);
  const [panels, setPanels] = useState([]);
  const [auditEvents, setAuditEvents] = useState([]);
  const [auditStats, setAuditStats] = useState({});
  const [chainStatus, setChainStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (!customerId) return;
    setLoading(true);
    try {
      const [c, q, h, m, nd, an, a, st, vr, fw, rcx, jgx, pnx] = await Promise.all([
        customerbasedshift(customerId, 'calibration_register').catch(() => []),
        customerbasedshift(customerId, 'operator_qualifications').catch(() => []),
        customerbasedshift(customerId, 'heat_treat_cycles').catch(() => []),
        customerbasedshift(customerId, 'material_certs').catch(() => []),
        customerbasedshift(customerId, 'ndt_inspections').catch(() => []),
        customerbasedshift(customerId, 'anodize_batches').catch(() => []),
        axios.get(`${BASE}/api/audit-log`, { params: { customer_id: customerId, limit: 12 } }).catch(() => ({ data: [] })),
        axios.get(`${BASE}/api/audit-log/stats`, { params: { customer_id: customerId } }).catch(() => ({ data: {} })),
        axios.get(`${BASE}/api/audit-log/verify`, { params: { customer_id: customerId, limit: 5000 } }).catch(() => ({ data: null })),
        customerbasedshift(customerId, 'follower_records').catch(() => []),
        customerbasedshift(customerId, 'route_cards').catch(() => []),
        customerbasedshift(customerId, 'jig_records').catch(() => []),
        customerbasedshift(customerId, 'panel_jobs').catch(() => []),
      ]);
      setCal(Array.isArray(c?.[0]?.value) ? c[0].value : []);
      setQuals(Array.isArray(q?.[0]?.value) ? q[0].value : []);
      setCycles(Array.isArray(h?.[0]?.value) ? h[0].value : []);
      setCerts(Array.isArray(m?.[0]?.value) ? m[0].value : []);
      setNdt(Array.isArray(nd?.[0]?.value) ? nd[0].value : []);
      setAnodize(Array.isArray(an?.[0]?.value) ? an[0].value : []);
      setFollowers(Array.isArray(fw?.[0]?.value) ? fw[0].value : []);
      setRouteCards(Array.isArray(rcx?.[0]?.value) ? rcx[0].value : []);
      setJigs(Array.isArray(jgx?.[0]?.value) ? jgx[0].value : []);
      setPanels(Array.isArray(pnx?.[0]?.value) ? pnx[0].value : []);
      setAuditEvents(a.data || []);
      setAuditStats(st.data || {});
      setChainStatus(vr.data);
    } finally { setLoading(false); }
  }, [customerId, BASE]);

  useEffect(() => { refresh(); }, [refresh]);

  const calStats = useMemo(() => {
    const out = { total: cal.length, valid: 0, warning: 0, overdue: 0 };
    cal.forEach(c => {
      const d = daysFromNow(c.next_cal_due_date);
      if (d == null || d < 0) out.overdue++;
      else if (d <= 30) out.warning++;
      else out.valid++;
    });
    return out;
  }, [cal]);

  const qualStats = useMemo(() => {
    const out = { total: quals.length, valid: 0, warning: 0, expired: 0 };
    quals.forEach(q => {
      const d = daysFromNow(q.expiry_date);
      if (d == null || d < 0) out.expired++;
      else if (d <= 30) out.warning++;
      else out.valid++;
    });
    return out;
  }, [quals]);

  const certStats = useMemo(() => {
    const out = { total: certs.length, valid: 0, warning: 0, expired: 0 };
    certs.forEach(c => {
      if (!c.expiry_date) { out.valid++; return; }
      const d = daysFromNow(c.expiry_date);
      if (d < 0) out.expired++;
      else if (d <= 30) out.warning++;
      else out.valid++;
    });
    return out;
  }, [certs]);

  const cycleStats = useMemo(() => {
    const out = { total: cycles.length, running: 0, accepted: 0, hold: 0, rejected: 0 };
    cycles.forEach(c => {
      if (c.status === 'RUNNING') out.running++;
      else if (c.status === 'COMPLETE_OK') out.accepted++;
      else if (c.status === 'COMPLETE_HOLD') out.hold++;
      else if (c.status === 'REJECTED') out.rejected++;
    });
    return out;
  }, [cycles]);

  const procStats = (rows) => {
    const out = { total: rows.length, running: 0, accepted: 0, hold: 0, rejected: 0 };
    rows.forEach(r => {
      if (r.status === 'RUNNING') out.running++;
      else if (r.status === 'COMPLETE_OK') out.accepted++;
      else if (r.status === 'COMPLETE_HOLD') out.hold++;
      else if (r.status === 'REJECTED') out.rejected++;
    });
    return out;
  };
  const ndtStats = useMemo(() => procStats(ndt), [ndt]);
  const anoStats = useMemo(() => {
    const s = procStats(anodize);
    s.panelFail = anodize.filter(a => a.test_panel_result === 'FAIL').length;
    return s;
  }, [anodize]);
  const followerStats = useMemo(() => {
    const out = { total: followers.length, inProgress: 0, done: 0, hold: 0 };
    followers.forEach(r => {
      if (r.status === 'HOLD') out.hold++;
      else if (r.status === 'DONE') out.done++;
      else out.inProgress++;
    });
    return out;
  }, [followers]);
  const builderStats = useMemo(() => { let gen = 0; routeCards.forEach(c => gen += (c.follower_ids || []).length); return { total: routeCards.length, gen }; }, [routeCards]);
  const panelStats = useMemo(() => ({ jigs: jigs.length, panels: panels.length, pass: panels.filter(p => p.adhesion_result === 'PASS').length, fail: panels.filter(p => p.adhesion_result === 'FAIL').length, pending: panels.filter(p => p.adhesion_result === 'PENDING').length }), [jigs, panels]);

  const tile = (status, label, big, subtle) => ({ status, label, big, subtle });

  // Compute headline status per area
  const tiles = useMemo(() => [
    {
      title: 'Calibration', icon: '🛠', link: '/calibration-register',
      headline: calStats.overdue > 0 ? 'ACTION' : calStats.warning > 0 ? 'REVIEW' : 'OK',
      stat: calStats.total,
      sub: `${calStats.valid} valid · ${calStats.warning} due ≤30d · ${calStats.overdue} overdue`,
      color: BR.blue,
    },
    {
      title: 'Operator Quals', icon: '🪪', link: '/operator-qualifications',
      headline: qualStats.expired > 0 ? 'ACTION' : qualStats.warning > 0 ? 'REVIEW' : 'OK',
      stat: qualStats.total,
      sub: `${qualStats.valid} valid · ${qualStats.warning} expiring · ${qualStats.expired} expired`,
      color: BR.purple,
    },
    {
      title: 'Heat Treat', icon: '🔥', link: '/heat-treat-cycles',
      headline: cycleStats.rejected > 0 ? 'ACTION' : cycleStats.hold > 0 ? 'REVIEW' : 'OK',
      stat: cycleStats.total,
      sub: `${cycleStats.accepted} accepted · ${cycleStats.running} running · ${cycleStats.hold} hold · ${cycleStats.rejected} rejected`,
      color: BR.orange,
    },
    {
      title: 'Material Certs', icon: '📜', link: '/material-certs',
      headline: certStats.expired > 0 ? 'ACTION' : certStats.warning > 0 ? 'REVIEW' : 'OK',
      stat: certStats.total,
      sub: `${certStats.valid} valid · ${certStats.warning} expiring · ${certStats.expired} expired`,
      color: BR.amber,
    },
    {
      title: 'NDT', icon: '🔎', link: '/ndt-inspections',
      headline: ndtStats.rejected > 0 ? 'ACTION' : ndtStats.hold > 0 ? 'REVIEW' : 'OK',
      stat: ndtStats.total,
      sub: `${ndtStats.accepted} accepted · ${ndtStats.running} in progress · ${ndtStats.hold} review · ${ndtStats.rejected} rejected`,
      color: BR.blue,
    },
    {
      title: 'Anodizing', icon: '🧪', link: '/anodize-batches',
      headline: (anoStats.rejected > 0 || anoStats.panelFail > 0) ? 'ACTION' : anoStats.hold > 0 ? 'REVIEW' : 'OK',
      stat: anoStats.total,
      sub: `${anoStats.accepted} accepted · ${anoStats.running} running · ${anoStats.hold} hold · ${anoStats.panelFail} panel-fail`,
      color: BR.orange,
    },
    {
      title: 'Route Card Follower', icon: '🧪', link: '/route-card-follower',
      headline: followerStats.hold > 0 ? 'ACTION' : followerStats.inProgress > 0 ? 'REVIEW' : 'OK',
      stat: followerStats.total,
      sub: `${followerStats.inProgress} in progress · ${followerStats.done} done · ${followerStats.hold} hold`,
      color: BR.blue,
    },
    {
      title: 'Route Card Builder', icon: '🗂', link: '/route-card-builder',
      headline: 'OK', stat: builderStats.total,
      sub: `${builderStats.gen} followers generated`,
      color: BR.blue,
    },
    {
      title: 'Jigging & Panels', icon: '🧷', link: '/jigging-panels',
      headline: panelStats.fail > 0 ? 'ACTION' : panelStats.pending > 0 ? 'REVIEW' : 'OK',
      stat: panelStats.panels,
      sub: `${panelStats.jigs} jigs · ${panelStats.pass} pass · ${panelStats.pending} pending · ${panelStats.fail} fail`,
      color: BR.orange,
    },
    {
      title: 'Audit Trail', icon: '🔒', link: '/audit-trail',
      headline: chainStatus && chainStatus.intact === false ? 'ACTION' : 'OK',
      stat: auditStats.total || 0,
      sub: chainStatus
        ? (chainStatus.intact ? `Chain intact (${chainStatus.checked} events)` : `⚠ Tampering at row #${chainStatus.first_bad_id}`)
        : 'Chain not verified',
      color: BR.green,
    },
  ], [calStats, qualStats, cycleStats, certStats, ndtStats, anoStats, followerStats, builderStats, panelStats, chainStatus, auditStats]);

  // Compliance score: simple weighted average across the 5 areas
  const score = useMemo(() => {
    const weights = { OK: 100, REVIEW: 70, ACTION: 30 };
    if (tiles.length === 0) return 0;
    return Math.round(tiles.reduce((s, t) => s + (weights[t.headline] || 0), 0) / tiles.length);
  }, [tiles]);

  const overdueCalItems = cal.filter(c => {
    const d = daysFromNow(c.next_cal_due_date);
    return d != null && d < 0;
  }).slice(0, 5);

  const expiredQualItems = quals.filter(q => {
    const d = daysFromNow(q.expiry_date);
    return d != null && d < 0;
  }).slice(0, 5);

  return (
    <div style={{ padding: 16, background: BR.bg, minHeight: '100vh' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 14 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 24, fontWeight: 900, color: BR.dark }}>
            🛡 NADCAP Compliance Cockpit
          </h1>
          <div style={{ fontSize: 12, color: BR.muted, marginTop: 4 }}>
            Audit-readiness across all PRD Common Core areas · Auto-refreshes on view
          </div>
        </div>
        <div style={{ background: '#fff', border: `2px solid ${score >= 90 ? BR.green : score >= 70 ? BR.amber : BR.red}`,
                       borderRadius: 8, padding: '12px 20px', textAlign: 'center', minWidth: 180 }}>
          <div style={{ fontSize: 10, fontWeight: 800, color: BR.muted, textTransform: 'uppercase', letterSpacing: 0.5 }}>
            Compliance Score
          </div>
          <div style={{ fontSize: 36, fontWeight: 900, color: score >= 90 ? BR.green : score >= 70 ? BR.amber : BR.red, lineHeight: 1.05 }}>
            {score}%
          </div>
          <div style={{ fontSize: 10, color: BR.muted, marginTop: 2 }}>
            {score >= 90 ? 'Audit-ready' : score >= 70 ? 'Findings likely' : 'Action needed'}
          </div>
        </div>
      </div>

      {/* Area tiles */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 10, marginBottom: 16 }}>
        {tiles.map(t => (
          <NavLink key={t.title} to={t.link}
                   style={{ textDecoration: 'none', display: 'block' }}>
            <div style={{ background: '#fff', border: `1.5px solid ${BR.border}`,
                            borderTop: `4px solid ${t.color}`, borderRadius: 8, padding: '12px 14px',
                            transition: 'transform 0.15s, box-shadow 0.15s', cursor: 'pointer' }}
                 onMouseEnter={e => {
                   e.currentTarget.style.transform = 'translateY(-2px)';
                   e.currentTarget.style.boxShadow = '0 4px 10px rgba(0,0,0,0.1)';
                 }}
                 onMouseLeave={e => {
                   e.currentTarget.style.transform = 'translateY(0)';
                   e.currentTarget.style.boxShadow = 'none';
                 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                <span style={{ fontSize: 18 }}>{t.icon}</span>
                <StatusPill status={t.headline} />
              </div>
              <div style={{ fontSize: 12, fontWeight: 800, color: BR.dark, marginTop: 4 }}>{t.title}</div>
              <div style={{ fontSize: 26, fontWeight: 900, color: t.color, lineHeight: 1.1, marginTop: 4 }}>{t.stat}</div>
              <div style={{ fontSize: 10, color: BR.muted, marginTop: 4, lineHeight: 1.3 }}>{t.sub}</div>
            </div>
          </NavLink>
        ))}
      </div>

      {/* Two-column body */}
      <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr', gap: 14 }}>
        {/* Findings */}
        <div style={{ background: '#fff', border: `1px solid ${BR.border}`, borderRadius: 8, padding: 14 }}>
          <div style={{ fontSize: 13, fontWeight: 800, color: BR.dark, marginBottom: 10 }}>
            ⚠ Open findings
          </div>
          {overdueCalItems.length === 0 && expiredQualItems.length === 0 ? (
            <div style={{ padding: 20, textAlign: 'center', color: BR.green, fontSize: 13, fontWeight: 700 }}>
              ✓ No overdue calibration or expired certifications. Good to go.
            </div>
          ) : (
            <>
              {overdueCalItems.length > 0 && (
                <div style={{ marginBottom: 14 }}>
                  <div style={{ fontSize: 11, fontWeight: 800, color: BR.red, textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 6 }}>
                    🛠 Overdue calibration ({overdueCalItems.length})
                  </div>
                  {overdueCalItems.map(c => (
                    <div key={c.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                              padding: '6px 10px', background: '#fef2f2', border: `1px solid #fecaca`,
                                              borderRadius: 5, marginBottom: 4 }}>
                      <div>
                        <div style={{ fontWeight: 800, fontSize: 12, fontFamily: 'monospace', color: BR.dark }}>{c.instrument_id}</div>
                        <div style={{ fontSize: 10, color: BR.muted }}>{c.instrument_type} · {c.location || '—'}</div>
                      </div>
                      <span style={{ padding: '2px 6px', background: BR.red, color: '#fff', borderRadius: 3,
                                      fontSize: 10, fontWeight: 800 }}>
                        OVERDUE {Math.abs(daysFromNow(c.next_cal_due_date))}d
                      </span>
                    </div>
                  ))}
                </div>
              )}
              {expiredQualItems.length > 0 && (
                <div>
                  <div style={{ fontSize: 11, fontWeight: 800, color: BR.red, textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 6 }}>
                    🪪 Expired certifications ({expiredQualItems.length})
                  </div>
                  {expiredQualItems.map(q => (
                    <div key={q.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                              padding: '6px 10px', background: '#fef2f2', border: `1px solid #fecaca`,
                                              borderRadius: 5, marginBottom: 4 }}>
                      <div>
                        <div style={{ fontWeight: 800, fontSize: 12, color: BR.dark }}>{q.operator_name}</div>
                        <div style={{ fontSize: 10, color: BR.muted }}>{q.certification}</div>
                      </div>
                      <span style={{ padding: '2px 6px', background: BR.red, color: '#fff', borderRadius: 3,
                                      fontSize: 10, fontWeight: 800 }}>
                        EXPIRED {Math.abs(daysFromNow(q.expiry_date))}d
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        {/* Recent audit events */}
        <div style={{ background: '#fff', border: `1px solid ${BR.border}`, borderRadius: 8, padding: 14 }}>
          <div style={{ fontSize: 13, fontWeight: 800, color: BR.dark, marginBottom: 10 }}>
            🔒 Recent activity
          </div>
          {auditEvents.length === 0 ? (
            <div style={{ padding: 20, textAlign: 'center', color: BR.muted, fontSize: 12 }}>
              No events yet. Start a WO operation to populate.
            </div>
          ) : (
            <div>
              {auditEvents.slice(0, 10).map(ev => {
                const color = ACTION_COLOR[ev.action] || BR.muted;
                return (
                  <div key={ev.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 8,
                                              padding: '6px 0', borderBottom: `1px solid ${BR.border}` }}>
                    <div style={{ width: 6, height: 6, borderRadius: 3, background: color,
                                   flexShrink: 0, marginTop: 6 }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 11, fontWeight: 800, color: BR.dark }}>
                        {ACTION_LABEL[ev.action] || ev.action}
                      </div>
                      <div style={{ fontSize: 10, color: BR.muted, marginTop: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {ev.entity_ref || ev.entity_id || '—'} · by {ev.actor_name || '—'}
                      </div>
                    </div>
                    <div style={{ fontSize: 10, color: BR.muted, fontFamily: 'monospace', whiteSpace: 'nowrap' }}>
                      {fmtDate(ev.ts)}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* PRD Core mapping */}
      <div style={{ background: '#fff', border: `1px solid ${BR.border}`, borderRadius: 8, padding: 14, marginTop: 14 }}>
        <div style={{ fontSize: 13, fontWeight: 800, color: BR.dark, marginBottom: 10 }}>
          📋 PRD Common Core mapping
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <tbody>
            {[
              ['1', 'Traceability / genealogy engine', 'OK',     'WO → ops → operator → machine → cycle'],
              ['2', 'IIoT parameter capture & lock',  'REVIEW', 'Capture live; recipe-lock pending'],
              ['3', 'Immutable electronic records',   'OK',     `${auditStats.total || 0} events · chain ${chainStatus?.intact ? 'intact' : 'unverified'}`],
              ['4', 'E-signature & approval workflow','OK',     'HT QA sign-off + audit hash chain'],
              ['5', 'Calibration & equipment mgmt',   calStats.overdue > 0 ? 'ACTION' : calStats.warning > 0 ? 'REVIEW' : 'OK', `${calStats.total} instruments`],
              ['6', 'Operator qualification register', qualStats.expired > 0 ? 'ACTION' : qualStats.warning > 0 ? 'REVIEW' : 'OK', `${qualStats.total} certs · warn gate active`],
              ['7', 'Non-conformance & RCCA',          'OK', 'NC Log + RCCA workflow'],
              ['8', 'Document & spec revision control','OK', 'ECN / ECR live'],
              ['9', 'Retention & audit-package builder','OK', 'Per-WO bundle export'],
            ].map(row => (
              <tr key={row[0]} style={{ borderBottom: `1px solid ${BR.border}` }}>
                <td style={{ padding: '8px 8px', width: 30, fontWeight: 800, color: BR.muted, fontFamily: 'monospace' }}>{row[0]}</td>
                <td style={{ padding: '8px 8px', fontWeight: 700, color: BR.dark }}>{row[1]}</td>
                <td style={{ padding: '8px 8px', width: 100 }}><StatusPill status={row[2]} /></td>
                <td style={{ padding: '8px 8px', color: BR.muted, fontSize: 11 }}>{row[3]}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {loading && (
        <div style={{ position: 'fixed', bottom: 16, right: 16, padding: '8px 14px',
                       background: BR.dark, color: '#fff', borderRadius: 6, fontSize: 12, fontWeight: 700 }}>
          Refreshing…
        </div>
      )}
    </div>
  );
}
